import React, { useEffect, useMemo, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import Cookies from "js-cookie";
import SelectTemplateModal from "../../WorkFlow/SelectTemplateModal";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import EditIcon from "@mui/icons-material/Edit";
import { Handle, Position } from "reactflow";
import { HandleDot } from "../handle";
import { useTheme } from "../../../contexts/ThemeContext";
import { ThemeProvider, createTheme } from "@mui/material/styles";

export const SendMessageEditModal = ({
  open,
  handleClose,
  data,
  appAccountId,
}) => {
  const { darkMode } = useTheme();
  const isEdit = !!data?.id;

  const theme = createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",
      primary: {
        main: "#0086ff",
      },
      background: {
        default: darkMode ? "#121212" : "#ffffff",
        paper: darkMode ? "#121212" : "#ffffff",
      },
      text: {
        primary: darkMode ? "#fafafa" : "#000000",
        secondary: darkMode ? "#fafafa" : "#666666",
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          contained: {
            backgroundColor: darkMode ? "#0086ff" : "#1976d2",
            "&:hover": {
              backgroundColor: darkMode ? "#0077ff" : "#1565c0",
            },
          },
          outlined: {
            borderColor: darkMode ? "#2f2f2f" : "#1976d2",
            color: darkMode ? "#fafafa" : "#1976d2",
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: darkMode ? "#2f2f2f" : "#cecece",
              },
              "&:hover fieldset": {
                borderColor: darkMode ? "#404040" : "#b0b0b0",
              },
              "&.Mui-focused fieldset": {
                borderColor: darkMode ? "#0086ff" : "#0086ff",
              },
            },
            "& .MuiInputLabel-root": {
              color: darkMode ? "#fafafa" : "#666666",
            },
            "& .MuiInputBase-input": {
              color: darkMode ? "#fafafa" : "#000000",
            },
          },
        },
      },
    },
  });

  const [accountId, setAccountId] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState(undefined);
  const [variables, setVariables] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState({});
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [contactFormData, setContactFormData] = useState();
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const authToken = `Token ${Cookies.get("auth_token")}`;

  useEffect(() => {
    if (isEdit) {
      const { templateId, selectedLanguage, accountId, selectedMediaUrl } =
        data?.data || {};

      setSelectedTemplate(templateId);
      setSelectedLanguage(selectedLanguage);
      setSelectedMedia(selectedMediaUrl);
      setAccountId(accountId);
    }
  }, [data?.data, isEdit]);

  const requestOptions = useMemo(() => ({
    method: "GET",
    headers: { Authorization: authToken },
    redirect: "follow",
  }), [authToken]);

  useEffect(() => {
    fetch(
      "https://api-test.phloz.app/contacts/Contactform",
      requestOptions
    )
      .then((res) => res.json())
      .then((data) => {
        setContactFormData(data.data);
      });
  }, [requestOptions]);

  useEffect(() => {
    const getTemplates = async () => {
      if (appAccountId !== undefined) {
        try {
          const response = await fetch(
            `https://api-test.phloz.app/whatsapp/templates?appAccountId=${appAccountId}`,
            {
              headers: {
                Authorization: `Token ${Cookies.get("auth_token")}`,
              },
            }
          );
          const responseData = await response.json();
          setTemplates(responseData.data);
        } catch (error) {
          console.error("Error fetching templates:", error);
        }
      }
    };

    getTemplates();
  }, [appAccountId]);

  const onAdd = (isNotEmpty) => {
    if (!isNotEmpty) {
      handleClose("-999");
    } else {
      if (selectedTemplate?.id) {
        handleClose(
          {
            templateId: selectedTemplate.id,
            selectedLanguage,
            accountId,
            appAccountId,
            selectedMedia,
          },
          isEdit
        );
      } else {
        alert("Please select template first");
      }
    }
  };

  // Wrapping the SelectTemplateModal in ThemeProvider
  return (
    <ThemeProvider theme={theme}>
      <SelectTemplateModal
        openModal={open}
        closeModal={(v) => onAdd(v)}
        templates={templates}
        accountid={accountId}
        appAccountId={appAccountId}
        setTemplateName={setTemplateName}
        templateName={templateName}
        variables={variables}
        setVariables={setVariables}
        selectedMedia={selectedMedia}
        setSelectedMedia={setSelectedMedia}
        contactFormData={contactFormData}
        setSelectedTemplate={setSelectedTemplate}
        selectedTemplate={selectedTemplate}
        isEdit={isEdit}
      />
    </ThemeProvider>
  );
};

export const SendMessageNode = (props) => {
  const { data, selected, isAddVisible, onAdd, onEdit, ...rest } = props;
  const { darkMode } = useTheme();

  // Applying the styling from DelayNode
  return (
    <div
      className={`w-40 shadow-md rounded-lg ${
        darkMode
          ? "bg-[#1e1e1e] border border-[#333333]"
          : "bg-white border-gray-200"
      } ${
        selected
          ? `border-solid border-2 ${
              darkMode ? "border-indigo-400" : "border-indigo-500/100"
            }`
          : ""
      } `}
    >
      <div className="flex flex-col">
        <div className="max-h-max px-2 py-1 text-left text-black text-xs font-bold rounded-t-md bg-teal-300">
          Send Message
          <Tooltip title={rest.id}>
            <ErrorOutlineIcon style={{ fontSize: 10 }} />
          </Tooltip>
          <Tooltip className="ml-2" title="Edit">
            <EditIcon style={{ fontSize: 12 }} onClick={() => onEdit(props)} />
          </Tooltip>
        </div>
        <div
          style={{
            fontSize: 11,
            padding: 6,
            color: darkMode ? "#fafafa" : "inherit",
          }}
        >
          Template:{" "}
          <span style={{ fontSize: 9, overflowWrap: "break-word" }}>
            {props.templateMap[data?.templateId] || data?.templateId}
          </span>
        </div>
      </div>

      <Handle id="b" type="target" position={Position.Left} />
      <HandleDot
        addVisible={true}
        id="b"
        type="source"
        position={Position.Right}
        onAdd={(v) => onAdd(v, rest)}
      />
    </div>
  );
};
